import React, { useEffect, useState } from 'react';

import ConfirmationPopup from 'components/ConfirmationPopup/ConfirmationPopup';
import { AddPicklistAssetPopup, AddAssemblyPopup } from 'components/Popups';
import MatchingLocation from 'components/ModalContents/MatchingLocation';

import {
  usePicklistActions,
  usePicklistPermissions,
  usePicklistDetailsSelector,
  usePicklistDetailsActions
} from 'hooks/Picklist';
import { useParams } from 'react-router-dom';
import { useMobileViewport } from 'hooks/useMobileViewport';
import { useRowSelection } from 'hooks/useRowSelection';
import { useHistory } from 'react-router-dom';
import { useManageLooseItemsActions } from 'hooks/ManageLooseItems';

import { PicklistStatusNames } from 'helpers/Enums';
import { isSuccessfulStatus, removeDuplicates } from 'helpers/AppHelpers';

import {
  DELETE_SELECTED_PICKLIST_ASSETS,
  DELETE_SELECTED_PICKLIST_ASSETS_TYPE,
  DELETE_SINGLE_PICKLIST_ASSET,
  DELETE_SINGLE_PICKLIST_ASSET_TYPE,
  PICKLIST_COMPLETED_STATUS,
  PICKLIST_COMPLETED_STATUS_TYPE,
  DELETE_FILE_FROM_PICKLIST_ASSET,
  DELETE_FILE_FROM_PICKLIST_ASSET_TYPE,
  DELETE_PICKLIST,
  PICKLIST_ASSETS_LOCATION_CHECK,
  PICKLIST_ASSETS_LOCATION_CHECK_TYPE
} from 'constants/dialogPopupsData';
import { MANAGE_LOOSE_ITEMS_PATH } from 'constants/routeConstants';

import { generateFilter, defaultTab, parseQuery, tabs } from './helpers';

import Wrapper from './components/Wrapper';
import BreadCrumbs from './components/BreadCrumbs';
import Header from './components/Header';
import PicklistInfo from './components/PicklistInfo';
import Tabs from './components/Tabs';
import TableControls from './components/TableControls';
import PicklistAssets from './components/Tables/PicklistAssets';
import PicklistLooseItems from './components/Tables/PicklistLooseItems';
import PicklistAssemblies from './components/Tables/PicklistAssemblies';

export function PicklistDetailed() {
  const { id } = useParams();
  const isMobile = useMobileViewport();
  const history = useHistory();

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [modalData, setModalData] = useState({});
  const [openAddAssetsPopup, setOpenAddAssetsPopup] = useState(false);
  const [openAddAssembliesPopup, setOpenAddAssembliesPopup] = useState(false);
  const [chips, setChips] = useState(false);

  const { currentPicklist, filter, picklistStatuses, assets, looseItems, assemblies } =
    usePicklistDetailsSelector();
  const { updatePicklistAction } = usePicklistActions();
  const picklistPermissions = usePicklistPermissions();
  const { isPicklistOpen } = picklistPermissions;
  const {
    getPicklistAction,
    getPicklistAssetsAction,
    getCountingAction,
    getFilterCriteriaAction,
    getPicklistStatusesAction,
    deletePicklistAction,
    deletePicklistAssetsAction,
    setFilterAction,
    setPicklistAssetsAction,
    setPicklistLooseItemsAction,
    setPicklistAssembliesAction,
    updatePicklistAssetAction,
    deleteFileFromAssetAction,
    checkPicklistAssetsLocationAction,
    addAssemblyToPicklistAction,
    clearStateAction
  } = usePicklistDetailsActions();
  const { setEntityDataAction } = useManageLooseItemsActions();

  const assetsSelectionState = useRowSelection({ items: assets });
  const looseItemsSelectionState = useRowSelection({ items: looseItems });
  const assembliesSelectionState = useRowSelection({ items: assemblies });
  const selectedRowIds = removeDuplicates([
    ...assetsSelectionState.selectedRows,
    ...looseItemsSelectionState.selectedRows,
    ...assembliesSelectionState.selectedRows
  ]);
  const resetSelectionsState = () => {
    assetsSelectionState.toggleAllSelection(false);
    looseItemsSelectionState.toggleAllSelection(false);
    assembliesSelectionState.toggleAllSelection(false);
  };

  const getPicklistAssets = (q = filter) => {
    setFilterAction({ ...q, responseReceived: false });
    getCountingAction(q);
    getPicklistAssetsAction(q).then((res) => {
      setPicklistAssetsAction(res?.assets || []);
      setPicklistLooseItemsAction(res?.looseItems || []);
      setPicklistAssembliesAction(res?.assembles || []);
      setFilterAction({ ...q, responseReceived: true });
    });
  };

  useEffect(() => {
    if (id) {
      getPicklistAction(id);
      getPicklistAssets(generateFilter(id));
      getFilterCriteriaAction({ picklistId: id });
      getPicklistStatusesAction();
    }
  }, []);

  useEffect(() => {
    return () => {
      clearStateAction();
    };
  }, []);

  const closeModal = () => setModalData({});
  const agreeModal = () => {
    switch (modalData.type) {
      case PICKLIST_COMPLETED_STATUS_TYPE:
        closeModal();
        updatePicklistAction({
          ...currentPicklist,
          status: picklistStatuses.find(({ name }) => name === PicklistStatusNames.completed)
        });
        break;
      case DELETE_SINGLE_PICKLIST_ASSET_TYPE:
      case DELETE_SELECTED_PICKLIST_ASSETS_TYPE: {
        closeModal();
        const ids = modalData?.deletedRowId ? [modalData.deletedRowId] : selectedRowIds;
        deletePicklistAssetsAction({ ids }).then((res) => {
          if (isSuccessfulStatus(res.status)) {
            getPicklistAssets();
            resetSelectionsState();
          }
        });
        break;
      }
      case DELETE_FILE_FROM_PICKLIST_ASSET_TYPE: {
        const { asset } = modalData;
        closeModal();
        deleteFileFromAssetAction(asset.resource.id).then((res) => {
          if (res.status !== 200) return;
          updatePicklistAsset({ ...asset, resource: null });
        });
        break;
      }
      case PICKLIST_ASSETS_LOCATION_CHECK_TYPE: {
        closeModal();
        updatePicklistAsset(modalData.editedRow);
        break;
      }
      default:
        deletePicklistAction(id);
        break;
    }
  };

  const openAssetsPopup = () => setOpenAddAssetsPopup(true);
  const openAssembliesPopup = () => setOpenAddAssembliesPopup(true);
  const removePicklist = () => setModalData({ ...DELETE_PICKLIST, isOpened: true });

  const applyFilter = (values) => {
    const parsedQuery = parseQuery({
      ...filter,
      filters: { picklistIds: [id], ...values }
    });
    getPicklistAssets(parsedQuery);
  };

  const changePicklistStatus = () => {
    if (!isPicklistOpen) {
      setModalData({ ...PICKLIST_COMPLETED_STATUS, isOpened: true });
      return;
    }

    updatePicklistAction({
      ...currentPicklist,
      status: picklistStatuses.find(({ name }) => name === PicklistStatusNames.delivered)
    });
  };

  const deleteSinglePicklistAsset = (deletedRowId) =>
    setModalData({ ...DELETE_SINGLE_PICKLIST_ASSET, deletedRowId, isOpened: true });
  const deleteSelectedPicklistAssets = () =>
    setModalData({ ...DELETE_SELECTED_PICKLIST_ASSETS, isOpened: true });

  const updatePicklistAsset = (row) =>
    updatePicklistAssetAction(row).then((res) => {
      if (!res.id) return;
      getPicklistAssets();
    });

  const deleteAssetFile = (asset) =>
    setModalData({ ...DELETE_FILE_FROM_PICKLIST_ASSET, isOpened: true, asset: asset });

  const showMatchingModal = (row, response) => {
    setModalData({
      ...PICKLIST_ASSETS_LOCATION_CHECK,
      editedRow: row,
      content: (
        <MatchingLocation
          items={response.assets}
          options={{
            itemType: 'asset',
            documentType: 'picklist',
            endpointType: response['isPicklistWithSublocation'] ? 'sublocation' : 'location'
          }}
        />
      )
    });
  };

  const handleEditDrCode = (row) => {
    if (!row?.asset?.id) return;

    const query = { picklistId: currentPicklist.id, assetIds: [row.asset.id] };
    checkPicklistAssetsLocationAction(query).then((response) => {
      if (response?.assets?.length) {
        showMatchingModal(row, response);
      } else {
        updatePicklistAsset(row);
      }
    });
  };

  const handleEditLooseItem = (row) => {
    const locationLooseItemDetails = {
      id: row.locationLooseItem.id,
      moveQuantity: row?.moveQuantity || 0,
      moveByAheadAccount: row?.moveByAheadAccount || 0,
      picklistAssetId: row?.id
    };

    setEntityDataAction({
      locationLooseItemDetails,
      activeEntity: currentPicklist,
      isPicklist: true
    });
    history.push(MANAGE_LOOSE_ITEMS_PATH);
  };

  const defaultTableProps = {
    isTableFiltered: !!chips?.length,
    getPicklistAssets: getPicklistAssets,
    updatePicklistAsset: updatePicklistAsset,
    onDeleteSingleAsset: deleteSinglePicklistAsset,
    onDeleteFile: deleteAssetFile
  };

  const handleAddAssembly = async (assembly) => {
    const payload = { assembledLooseItem: assembly, picklist: currentPicklist };
    await addAssemblyToPicklistAction(payload);
    getPicklistAssets();
  };

  const changeSelectedItemsStatus = async (status) => {
    const updateItemsStatus = (items, status, selectedIds) =>
      items.map((item) => (selectedIds.includes(item.id) ? { ...item, status } : item));

    const updatedAssets = updateItemsStatus(assets, status, selectedRowIds);
    const updatedLooseItems = updateItemsStatus(looseItems, status, selectedRowIds);
    const updatedAssemblies = updateItemsStatus(assemblies, status, selectedRowIds);

    const result = await updatePicklistAction({
      ...currentPicklist,
      picklistAssets: {
        ...currentPicklist.picklistAssets,
        assets: updatedAssets || [],
        looseItems: updatedLooseItems || [],
        assembles: updatedAssemblies || []
      }
    });

    if (result.id) {
      applyFilter();
      resetSelectionsState();
    }
  };

  return currentPicklist?.id ? (
    <Wrapper>
      {!isMobile && <BreadCrumbs />}

      <Header
        onAddAssets={openAssetsPopup}
        onAddAssembly={openAssembliesPopup}
        onRemovePicklist={removePicklist}
        onStatusChange={changePicklistStatus}
      />

      <PicklistInfo />

      <TableControls
        chips={chips}
        selectedRowIds={selectedRowIds}
        setChips={setChips}
        onApply={changeSelectedItemsStatus}
        onFilterApply={applyFilter}
        onDeleteSelectedAssets={deleteSelectedPicklistAssets}
      />

      <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />

      {activeTab === tabs.assets && (
        <PicklistAssets
          selectionState={assetsSelectionState}
          onEditDrCode={handleEditDrCode}
          {...defaultTableProps}
        />
      )}

      {activeTab === tabs.looseItems && (
        <PicklistLooseItems
          selectionState={looseItemsSelectionState}
          onEditLooseItem={handleEditLooseItem}
          {...defaultTableProps}
        />
      )}

      {activeTab === tabs.assemblies && (
        <PicklistAssemblies selectionState={assembliesSelectionState} {...defaultTableProps} />
      )}

      <ConfirmationPopup data={modalData} onAgree={agreeModal} onDismiss={closeModal} />

      {openAddAssetsPopup && (
        <AddPicklistAssetPopup
          open={openAddAssetsPopup}
          setOpen={setOpenAddAssetsPopup}
          screen="Picklist Details"
          picklist={currentPicklist}
          onSuccess={getPicklistAssets}
        />
      )}

      {openAddAssembliesPopup && (
        <AddAssemblyPopup
          open={openAddAssembliesPopup}
          setOpen={setOpenAddAssembliesPopup}
          screen="Picklist Detailed"
          onAdd={handleAddAssembly}
        />
      )}
    </Wrapper>
  ) : null;
}
